const STORAGE_KEY_PREFIX = "wildlark:workspace-sidebar:v1"

export interface WorkspaceSidebarState {
    archivedVisible: boolean
    hiddenWorkspaceIds: string[]
    workspaceOrder: string[]
}

function uniqueStrings(value: unknown): string[] | null {
    if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) return null
    return [...new Set(value)]
}

function parseWorkspaceSidebarState(value: unknown): WorkspaceSidebarState | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null

    const workspaceOrder = uniqueStrings(Reflect.get(value, "workspaceOrder"))
    const archivedVisible = Reflect.get(value, "archivedVisible")
    const hiddenWorkspaceIdsValue = Reflect.get(value, "hiddenWorkspaceIds")
    const hiddenWorkspaceIds = hiddenWorkspaceIdsValue === undefined ? [] : uniqueStrings(hiddenWorkspaceIdsValue)
    if (!hiddenWorkspaceIds || !workspaceOrder || typeof archivedVisible !== "boolean") return null

    return { archivedVisible, hiddenWorkspaceIds, workspaceOrder }
}

function storageKey(scope: string): string {
    return `${STORAGE_KEY_PREFIX}:${scope}`
}

export function loadWorkspaceSidebarState(scope: string): WorkspaceSidebarState | null {
    if (typeof window === "undefined") return null

    try {
        const serialized = window.localStorage.getItem(storageKey(scope))
        if (!serialized) return null
        const value: unknown = JSON.parse(serialized)
        return parseWorkspaceSidebarState(value)
    } catch {
        return null
    }
}

export function saveWorkspaceSidebarState(scope: string, state: WorkspaceSidebarState): void {
    if (typeof window === "undefined") return

    try {
        window.localStorage.setItem(storageKey(scope), JSON.stringify(state))
    } catch {
        // Sidebar preferences are best-effort when storage is unavailable.
    }
}

export function orderWorkspaces<T extends { id: string }>(workspaces: T[], workspaceOrder: string[]): T[] {
    const savedIndexById = new Map(workspaceOrder.map((id, index) => [id, index]))
    return workspaces
        .map((workspace, originalIndex) => ({ originalIndex, savedIndex: savedIndexById.get(workspace.id), workspace }))
        .sort((a, b) => {
            if (a.savedIndex !== undefined && b.savedIndex !== undefined) return a.savedIndex - b.savedIndex
            if (a.savedIndex !== undefined) return -1
            if (b.savedIndex !== undefined) return 1
            return a.originalIndex - b.originalIndex
        })
        .map(({ workspace }) => workspace)
}

export function hiddenWorkspaceIdsForOnly(activeWorkspaceIds: string[], workspaceId: string): string[] {
    return activeWorkspaceIds.filter((id) => id !== workspaceId)
}

export function moveWorkspace(workspaceOrder: string[], sourceWorkspaceId: string, targetWorkspaceId: string, edge: "before" | "after"): string[] {
    if (sourceWorkspaceId === targetWorkspaceId) return [...workspaceOrder]

    const next = workspaceOrder.filter((id) => id !== sourceWorkspaceId)
    const targetIndex = next.indexOf(targetWorkspaceId)
    if (targetIndex < 0) return [...workspaceOrder]

    next.splice(edge === "before" ? targetIndex : targetIndex + 1, 0, sourceWorkspaceId)
    return next
}
